import {useMemo, useState} from 'react'
import {useQuery} from '@apollo/client'
import {Breadcrumb, Button, Col, Form, InputGroup, Row, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {GET_STATES_EU} from '../../gql/queries/statesEUQuery'
import {useDisclourse} from '../../hooks/useDisclourse'
import {ModalEditStateEU} from './components/ModalEditStateEU'
import {IResStates, NodeStateEU} from '../../types/statesEU'

const JURISDICTION_LABELS: Record<string, string> = {
  FEDERAL: 'Federal',
  STATE: 'State Exchange',
}

export const StatesEUPage = () => {
  const {data, loading, error, refetch} = useQuery<IResStates>(GET_STATES_EU, {
    fetchPolicy: 'no-cache',
    onError: (err) => toast.error(`Error: ${err.message}`),
  })
  const {isOpen, onClose, onOpen} = useDisclourse()
  const [selectedState, setSelectedState] = useState<NodeStateEU | undefined>()
  const [text, setText] = useState('')
  const [filterJurisdiction, setFilterJurisdiction] = useState('')

  const states = useMemo(() => {
    const rows = data?.allStates?.edges.map(({node}) => node) || []
    const normalizedText = text.trim().toLowerCase()

    return rows.filter((s) => {
      const matchesText =
        !normalizedText ||
        [s.name, s.code].some((v) => v?.toLowerCase().includes(normalizedText))
      const matchesJurisdiction =
        !filterJurisdiction || s.jurisdictionType === filterJurisdiction
      return matchesText && matchesJurisdiction
    })
  }, [data, text, filterJurisdiction])

  if (loading && !data) {
    return (
      <div>
        <Spinner animation='border' className='ms-4' />
      </div>
    )
  }

  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='d-blok d-md-none'>
            <span>
              <i className='bi bi-list fs-1' />
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>US States</Breadcrumb.Item>
        </Breadcrumb>
      </header>
      <Row className='policies'>
        <Col md={12} lg={12} sm={12} xl={12} className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>US States</h3>
            </div>
            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder='Search by name or code...'
                  onChange={(e) => setText(e.target.value)}
                  value={text}
                />
                <Form.Select
                  value={filterJurisdiction}
                  onChange={(e) => setFilterJurisdiction(e.target.value)}
                  style={{maxWidth: '200px'}}
                >
                  <option value=''>All jurisdictions</option>
                  <option value='FEDERAL'>Federal</option>
                  <option value='STATE'>State Exchange</option>
                </Form.Select>
                <Button
                  onClick={() => refetch()}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  Refresh
                </Button>
              </InputGroup>
            </div>
            <div className='results-header'>
              <div className='px-2 py-2 fst-italic'>
                Showing {states.length} of {data?.allStates?.edges.length || 0} states
              </div>
            </div>
            <div className='list-carrier table-scrollable table-container'>
              <table className='table-scrollable table-scroll table table-striped table-hover'>
                <thead>
                  <tr role='row'>
                    <th className='fw-bold'>Code</th>
                    <th className='fw-bold'>Name</th>
                    <th className='fw-bold text-center'>Jurisdiction</th>
                    <th className='fw-bold'>ACA Quote URL</th>
                    <th className='fw-bold text-center'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {states.map((state) => (
                    <tr key={state.id}>
                      <td className='px-4 fw-semibold'>{state.code}</td>
                      <td className='px-2'>{state.name}</td>
                      <td className='px-2 text-center'>
                        {state.jurisdictionType ? (
                          <span
                            className={`badge text-white ${
                              state.jurisdictionType === 'STATE' ? 'bg-success' : 'bg-primary'
                            }`}
                          >
                            {JURISDICTION_LABELS[state.jurisdictionType]}
                          </span>
                        ) : (
                          <span className='badge bg-secondary'>Not set</span>
                        )}
                      </td>
                      <td className='px-2'>
                        {state.urlAcaQuote ? (
                          <a
                            href={state.urlAcaQuote}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-truncate d-inline-block'
                            style={{maxWidth: '300px'}}
                            title={state.urlAcaQuote}
                          >
                            {state.urlAcaQuote}
                          </a>
                        ) : (
                          <span className='text-muted fst-italic'>—</span>
                        )}
                      </td>
                      <td className='px-2 text-center'>
                        <button
                          title='Edit State'
                          style={{border: 0, backgroundColor: 'transparent', padding: 0, margin: 0}}
                          onClick={() => {
                            setSelectedState(state)
                            onOpen()
                          }}
                        >
                          <i className='bi bi-pencil-fill text-primary' />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {states.length === 0 && !error && (
                    <tr>
                      <td colSpan={5} className='text-center text-muted py-5'>
                        No states found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && selectedState && (
        <ModalEditStateEU
          isOpen={isOpen}
          onClose={onClose}
          info={selectedState}
          refetch={refetch}
        />
      )}
    </div>
  )
}
